/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useState} from 'react';
import {service as FileUploadService} from "../Services/FileUploadService";
import {FileUploadOverlay} from "../Components/Common/Files";
import {useDefaultErrorHandler} from "./ErrorHooks";
import {useEditContextNodeId} from "./PermissionHooks";
import {FileDraggingOverlay} from "../Components/Common/Files/FileDraggingOverlay";
import {useDropzone} from "react-dropzone";
import {useFetchContextNode} from "./ContextHooks";
import {useFileEditor} from "../Components/Common/Files/useFileEditor";

// Custom hook for file browser functionality
export const useStreamBankFileDrop = (contextNodeId, options = {}) => {
    
    const {
        onSuccess = null,
        onError = null,
        surveyTypeId = null,
        tagCode = null,
        acceptedFileTypes = "",
    } = options;
    
    const defaultErrorHandler = useDefaultErrorHandler();
    const canEditContextNodeId = useEditContextNodeId();
    
    const contextNode = useFetchContextNode(contextNodeId);
    
    const fileUploadInputRef = React.useRef(null);
    
    const defaultSuccessHandler = (files) => {
        // If we have a single file, show the editor.
        if (files.length === 1) {
            const file = files[0];
            if (file) {
                showFileEditor(file);
            }
        }
    }
    const successHandler = onSuccess ?? defaultSuccessHandler;
    const errorHandler = onError ?? defaultErrorHandler;
    
    const onFileChanged = (file) => {
        if (file) {
            setLastChanged(new Date());
        }
    }
    
    const [showFileEditor, hideFileEditor, fileEditorComponent] = useFileEditor(onFileChanged);
    
    // const canEditFileUpload = useEditFileUpload();
    
    const [displayUpload, setDisplayUpload] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    
    const [uploadProgress, setUploadProgress] = useState([]);
    
    // components can watch for this value to change
    // and use it to refetch the data
    const [lastChanged, setLastChanged] = useState(null);
    
    const onFileDrop = async (files) => {
        if (canEditContextNodeId(contextNode?.contextNodeId)) {
            setDisplayUpload(true);
            setUploadingFiles(files);
            
            const fileProgress = new Array(files.length).fill(0);
            
            const uploadPromises = files.map((file, index) =>
                FileUploadService.UploadFile(
                    file.name,
                    file,
                    [contextNode.contextNodeId],
                    (a, b) => {
                        fileProgress[index] = (a / b) * 100;
                        setUploadProgress(fileProgress);
                    },
                    surveyTypeId,
                    tagCode
                )
            );
            
            //when all the files are uploaded hide the dialog.
            try {
                const results = await Promise.all(uploadPromises);
                successHandler(results);
            } catch (error) {
                errorHandler(error);
            } finally {
                setUploadingFiles(false);
                setDisplayUpload(false);
                setUploadProgress([]);
                setLastChanged(new Date());
            }
        } else {
            //Shouldn't be able to get here.
            alert(`You don't have permissions to upload a file to ${contextNode?.name ?? 'this context'}.`);
        }
    };
    
    const dropZone =  useDropzone({
        onDrop: onFileDrop,
        noClick: true,
        noKeyboard: true
    });
    
    const {
        isDragActive,
        rootRef,
        inputRef,
        getRootProps,
        getInputProps
    } = dropZone;
    
    const fileDraggingOverlay = <FileDraggingOverlay
        key="file-dragging-overlay"
        show={isDragActive}
        contextNode={contextNode}
    />
    
    const fileUploadOverlay = <FileUploadOverlay
            key="file-upload-overlay"
            show={displayUpload}
            uploadingFiles={uploadingFiles ? uploadingFiles.length : false}
            fileProgress={uploadProgress}
        />;
    
    const fileUploadInput = <input
            key="file-upload-input"
            {...getInputProps()}
            ref={fileUploadInputRef}
            type="file"
            accept={acceptedFileTypes}
            style={{ display: "none" }}
            onChange={(e) => {
                // FileLists aren't arrays and can't be iterated, so convert it
                onFileDrop(Array.from(e.target.files || []));
            }}
            multiple
        />;
    
    const fileUploadComponents = [fileDraggingOverlay, fileUploadOverlay, fileEditorComponent, fileUploadInput];
    
    const showFilePicker = () => {
        fileUploadInputRef?.current?.click?.();
    }
    
    return {
        ...dropZone,
        
        fileUploadComponents,
        lastChanged,
    
        onFileDrop,
        uploadingFiles,
        contextNode,
        uploadProgress,
        showFilePicker,
    };
};
